import { PayloadAction } from '@reduxjs/toolkit';
import { ReactNode } from 'react';

/**
 * Redux action type for API requests
 */
export type ApiAction<T = any> = (params: T) => Promise<PayloadAction<any>>;

/**
 * Items data type
 */
export interface ItemsData<T> {
  readonly items: T[];
}

/**
 * Pagination params
 */
export interface PaginationParams {
  page_number?: number;
  page_size?: number;
  query?: string
}

/**
 * Loading states
 */
export type LoadingState = 'idle' | 'loading' | 'error' | 'complete';

/**
 * Scroll container options
 */
export interface ScrollOptions {
  threshold?: number;
  scrollContainer?: string | HTMLElement | null;
  debounceMs?: number;
}

/**
 * Loading config
 */
export interface LoadingConfig {
  showSpinner?: boolean;
  component?: ReactNode;
  text?: string;
  animationDuration?: number;
}

/**
 * Search config
 */
export interface SearchConfig {
  searchValue?: string;
  resetOnChange?: boolean;
  debounceMs?: number;
}

/**
 * Retry configuration
 */
export interface RetryConfig {
  enabled?: boolean;
  maxAttempts?: number;
  delay?: number;
}

/**
 * Infinite scroll config
 */
export interface InfiniteScrollConfig<T = any, P = any, S = any> {
  /** Redux action */
  apiAction: ApiAction<P>;

  /** Items data from state */
  itemsData: ItemsData<T> | T[];

  /** Initial page size */
  initialPageSize?: number;

  /** Starting page number */
  startPage?: number;

  /** Scroll detection options */
  scroll?: ScrollOptions;

  /** Loading config */
  loading?: LoadingConfig;

  /** Search config */
  search?: SearchConfig;

  /** More params for filters */
  apiParams?: P;

  /** Enable debug */
  debug?: boolean;

  /** Auto-load first page */
  autoLoadFirst?: boolean;

  useDirectResponse?: boolean;

  /** Max pages  */
  maxPages?: number;

  /** Retry configuration */
  retry?: RetryConfig;

  /** Additional parameters */
  params?: any;
}

/**
 * Infinite scroll state
 */
export interface InfiniteScrollState {
  currentPage: number;
  loading: LoadingState;
  hasMore: boolean;
  error: string | null;
  totalLoaded: number;
  searchHash?: string | null;
}

/**
 * Scroll state information
 */
export interface ScrollState {
  isNearBottom: boolean;
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  container: HTMLElement | Window | null;
}

/**
 * Scroll detection hook return type
 */
export interface ScrollDetectionReturn {
  scrollState: ScrollState;
  forceCheck: () => void;
  scrollToTop: () => void;
  scrollToBottom: () => void;
  attachSentinel: (element: HTMLElement | null) => void;
  isNearBottom: boolean;
  container: HTMLElement | Window | null;
}

/**
 * UseInfiniteScroll hook return type
 */
export interface UseInfiniteScrollReturn<T> {
  items: T[];
  state: InfiniteScrollState;
  loadMore: () => Promise<void>;
  reset: () => void;
  refresh: () => Promise<void>;
  retry: () => void;
  isLoading: boolean;
  hasMore: boolean;
  error: string | null;
  scrollDetection: ScrollDetectionReturn;
}

/**
 * Infinite scroll component props
 */
export interface InfiniteScrollProps<T, P = any, S = any> extends InfiniteScrollConfig<T, P, S> {
  children: (items: T[], state: InfiniteScrollState) => ReactNode;
  className?: string;
  style?: React.CSSProperties;

  renderError?: (error: string, retry: () => void) => ReactNode;
  renderEmpty?: () => ReactNode;
  renderLoading?: (isInitial: boolean) => ReactNode;

  params?: any;
}

/**
 * Default configuration for infinite scroll
 */
export const DEFAULT_CONFIG: Required<Omit<InfiniteScrollConfig, 'apiAction' | 'itemsData' | 'apiParams' | 'search'>> = {
  initialPageSize: 40,
  startPage: 1,
  scroll: {
    threshold: 200,
    scrollContainer: null,
    debounceMs: 150,
  },
  loading: {
    showSpinner: true,
    text: 'Loading more...',
    animationDuration: 300,
  },
  useDirectResponse: false,
  debug: false,
  autoLoadFirst: true,
  maxPages: 0,
  retry: {
    enabled: true,
    maxAttempts: 3,
    delay: 1000,
  },
  params: {},
};

/**
 * Extract item type from ItemsData
 */
export type ExtractItemType<T> = T extends ItemsData<infer U> ? U : never;

/**
 * Scroll detection hook props
 */
export interface UseScrollDetectionProps extends ScrollOptions {
  onReachThreshold: () => void;
  enabled?: boolean;
}

/**
 * Scroll container ref hook return type
 */
export interface UseScrollContainerRefReturn {
  containerRef: React.RefObject<HTMLElement>;
  scrollContainer: HTMLElement | null;
}